import { JobWorkflowStatusEnum } from 'domains/workspaceDomain';

export enum JobCardActionEnum {
  AdjustJob = 'adjust',
  CancelJob = 'cancelJob',
  UpdateJob = 'updateJob',
  SendToRFM = 'sendRfm',
}

const { AdjustJob, UpdateJob, CancelJob, SendToRFM } = JobCardActionEnum;

const {
  PO_SENT,
  PO_READ,
  PO_ACCEPT,
  RFM,
  PREPARE_PROOF,
  PROOF_AWAIT_APPROVAL,
  PROOF_NEED_REVISION,
  IN_PRODUCTION,
  SHIPPED,
  DELIVERED,
  INVOICING,
  COMPLETED,
} = JobWorkflowStatusEnum;

export type JobCardActionContext = {
  workflowStatus: JobWorkflowStatusEnum;
  onlyOneParticipant: boolean;
  hasCustomer: boolean;
  hasProxy: boolean;
  hasOperator: boolean;
  hasProducer: boolean;
  isInternalJob: boolean;
};

/**
 * Which actions the job card offers, by participant mix and workflow status (REV-8273).
 *
 * Lives apart from the Actions menu that renders it because the menu is no longer the only place
 * an action can surface: Canva orgs also get Cancel Job as a link on the card itself, and the two
 * have to agree on when it is offered. Availability only -- the caller supplies the handlers.
 */
export const getJobCardActions = ({
  workflowStatus,
  onlyOneParticipant,
  hasCustomer,
  hasProxy,
  hasOperator,
  hasProducer,
  isInternalJob,
}: JobCardActionContext): JobCardActionEnum[] => {
  let filterActionMaps: {
    [key in JobCardActionEnum]: {
      byHide: boolean;
      filter: Partial<JobWorkflowStatusEnum>[];
    };
  } = {
    [AdjustJob]: {
      byHide: false,
      filter: [],
    },
    [SendToRFM]: {
      byHide: false,
      filter: [],
    },
    [CancelJob]: {
      byHide: false,
      filter: [],
    },
    [UpdateJob]: {
      byHide: false,
      filter: [],
    },
  };
  if (
    (hasCustomer && hasOperator) ||
    (hasProxy && hasOperator && !isInternalJob) ||
    (onlyOneParticipant && hasOperator && !isInternalJob)
  ) {
    filterActionMaps = {
      [AdjustJob]: {
        byHide: true,
        filter: [],
      },
      [SendToRFM]: {
        byHide: true,
        filter: [RFM, SHIPPED, DELIVERED, INVOICING],
      },
      [CancelJob]: {
        byHide: true,
        filter: [SHIPPED, DELIVERED, INVOICING],
      },
      [UpdateJob]: {
        byHide: false,
        filter: [],
      },
    };
  } else if (
    (hasProxy && hasOperator && isInternalJob) ||
    (onlyOneParticipant && hasOperator && isInternalJob)
  ) {
    filterActionMaps = {
      [AdjustJob]: {
        byHide: true,
        filter: [PO_SENT, PO_READ, RFM],
      },
      [SendToRFM]: {
        byHide: false,
        filter: [],
      },
      [CancelJob]: {
        byHide: false,
        filter: [
          PO_ACCEPT,
          PREPARE_PROOF,
          PROOF_AWAIT_APPROVAL,
          PROOF_NEED_REVISION,
          IN_PRODUCTION,
        ],
      },
      [UpdateJob]: {
        byHide: false,
        filter: [],
      },
    };
  } else if (onlyOneParticipant && hasProducer && !isInternalJob) {
    filterActionMaps = {
      [AdjustJob]: {
        byHide: true,
        filter: [PO_SENT, PO_READ, RFM],
      },
      [SendToRFM]: {
        byHide: false,
        filter: [],
      },
      [CancelJob]: {
        byHide: false,
        filter: [],
      },
      [UpdateJob]: {
        byHide: false,
        filter: [],
      },
    };
  } else if (
    (onlyOneParticipant && hasProducer && isInternalJob) ||
    (hasProxy && hasProducer && isInternalJob)
  ) {
    filterActionMaps = {
      [AdjustJob]: {
        byHide: true,
        filter: [PO_SENT, PO_READ, RFM],
      },
      [SendToRFM]: {
        byHide: false,
        filter: [],
      },
      [CancelJob]: {
        byHide: false,
        filter: [
          PREPARE_PROOF,
          PROOF_AWAIT_APPROVAL,
          PROOF_NEED_REVISION,
          IN_PRODUCTION,
        ],
      },
      [UpdateJob]: {
        byHide: false,
        filter: [
          PREPARE_PROOF,
          PROOF_AWAIT_APPROVAL,
          PROOF_NEED_REVISION,
          IN_PRODUCTION,
        ],
      },
    };
  }
  if (workflowStatus === COMPLETED) {
    return [];
  }
  return (Object.keys(filterActionMaps) as JobCardActionEnum[]).filter(
    (key) => {
      const { byHide, filter } = filterActionMaps[key];
      return byHide
        ? !filter?.includes(workflowStatus)
        : filter?.includes(workflowStatus);
    }
  );
};
